import IProcedureDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureDetail';
import React, { ChangeEvent, useState } from 'react';
import ProcedureActionEditor from './ProcedureActionEditor';
import {
  EditorChangeEvent,
  EditorMountEvent,
  EditorTools
} from '@Shared.Angular/kendo.react/KendoReactEditor';
import KendoReactEditor from '@Shared.Angular/kendo.react/KendoReactEditor';
import { getService } from '@Client/runner.hooks/useService';
import IProcedureActionDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionDetail';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import ClipboardCopy from '@Shared.Angular/flowingly.components/ClipboardCopy/ClipboardCopy';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import PublishProcedureModal from './PublishProcedureModal';
import ProcedureDetailsModal from './ProcedureDetailsModal';

type Props = {
  procedure: IProcedureDetail;
  readOnly?: boolean;
  isPublicView?: boolean;
};

const ProcedureEditor = (props: Props) => {
  const readOnly = props.readOnly || false;
  const isPublicView = props.isPublicView || false;
  const permissionsService = getService('permissionsService');
  const canPublish = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.PROCEDURE_PUBLISH
  );
  const canEdit = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.PROCEDURE_UPDATE
  );

  const [procedure, setProcedure] = useState(() => props.procedure);
  const [editedProcedure, setEditedProcedure] = useState({ ...procedure });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isMobile] = useState<boolean>(
    getService('browserUtilsService').isMobileDevice()
  );
  const [imageViewIndex, setImageViewIndex] = useState<number>(-1);

  const updateName = (e: ChangeEvent<HTMLInputElement>) => {
    setEditedProcedure({ ...editedProcedure, name: e.target.value });
  };
  const updateDescription = (e: EditorChangeEvent) => {
    setEditedProcedure({ ...editedProcedure, description: e.html });
  };
  const procedureApiService = getService('procedureApiService');
  const $state = getService('$state');

  const saveChanges = () => {
    procedureApiService.updateProcedure(editedProcedure);
    setProcedure({ ...editedProcedure });
    setIsEditMode(false);
  };
  const cancelChanges = () => {
    setEditedProcedure({ ...procedure });
    setIsEditMode(false);
  };
  const actionUpdated = (updatedAction: IProcedureActionDetail) => {
    setProcedure((prev) => ({
      ...prev,
      actions: prev.actions.map((action) =>
        action.order === updatedAction.order ? updatedAction : action
      )
    }));
  };

  const addEditorContentStyles = (event: EditorMountEvent) => {
    const iframeDocument = event.dom.ownerDocument;
    const style = iframeDocument.createElement('style');
    style.appendChild(
      iframeDocument.createTextNode(
        'body{font-family:"Open Sans", sans-serif;font-size:13px;}'
      )
    );
    iframeDocument.head.appendChild(style);
  };

  const tocItemClicked = (actionId: Guid) => {
    document
      .getElementById('action_' + actionId)
      .scrollIntoView({ behavior: 'smooth' });
  };

  const tocKeyPress = (event: React.KeyboardEvent, actionId: Guid) => {
    if (event.key === 'Enter') {
      tocItemClicked(actionId);
    }
  };

  const handlePublishedProcedure = (publishedProcedure: IProcedureDetail) => {
    setProcedure(publishedProcedure);
    setEditedProcedure(publishedProcedure);
    setIsPublishModalOpen(false);
  };

  const addAction = (afterIndex: number, order: number) => {
    const newAction = {
      id: EmptyGuid,
      procedureId: procedure.id,
      procedureSchemaId: procedure.actions[0]?.procedureSchemaId || EmptyGuid,
      title: '',
      description: '',
      order: order,
      active: true
    } as IProcedureActionDetail;
    const afterActions = procedure.actions.slice(afterIndex + 1);
    afterActions.forEach((action) => {
      action.order++;
    });
    setProcedure((prev) => ({
      ...prev,
      actions: [
        ...prev.actions.slice(0, afterIndex + 1),
        newAction,
        ...afterActions
      ]
    }));
  };

  const goToEditMode = () => {
    // We redirect instead of turning off readOnly
    // because there may be draft changes not currently loaded
    $state.go(
      'app.runner.procedure',
      { procedureId: procedure.id, edit: true },
      {}
    );
  };

  const imageViewNavigationChange = (newUiIndex: number) => {
    if (newUiIndex === null) {
      setImageViewIndex(-1);
    }
    // newIndex is the 1-based index shown in the UI
    const arrayIndex = newUiIndex - 1;
    if (arrayIndex < 0 || arrayIndex >= procedure.actions.length) {
      return;
    }
    setImageViewIndex(arrayIndex);
  };

  const editorTools = [
    EditorTools.Bold,
    EditorTools.Italic,
    EditorTools.Underline,
    EditorTools.Strikethrough,
    EditorTools.ForeColor,
    EditorTools.BackColor,
    EditorTools.CleanFormatting,
    EditorTools.AlignLeft,
    EditorTools.AlignCenter,
    EditorTools.AlignRight,
    EditorTools.AlignJustify,
    EditorTools.Indent,
    EditorTools.Outdent,
    EditorTools.UnorderedList,
    EditorTools.FontSize,
    EditorTools.FormatBlock,
    EditorTools.Link,
    EditorTools.Unlink
  ];

  return (
    procedure && (
      <div
        id="editor-container"
        className={
          (isMobile ? 'mobile ' : '') +
          (readOnly ? 'read-only ' : '') +
          (isPublicView ? 'public-view' : '')
        }
      >
        {isPublicView && (
          <h3 className="public-title">Flowingly SOP Recorder</h3>
        )}

        {!isMobile && (
          <div className="editors-left-column">
            <div className="table-of-contents">
              <div className="toc-heading">Table of Contents</div>
              <div className="toc-items">
                {procedure.actions
                  .filter((action) => action.active)
                  .map((action, index) => (
                    <div
                      className="toc-item"
                      key={action.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => tocItemClicked(action.id)}
                      onKeyDown={(e) => tocKeyPress(e, action.id)}
                    >
                      <div className="toc-index">
                        {index < 9 ? '0' : ''}
                        {index + 1}
                      </div>
                      <div className="toc-title">{action.title}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
        <div className="editors">
          {isEditMode ? (
            <div className="editor">
              <div className="procedure-field">
                <label htmlFor="procedure-name">Name</label>
                <input
                  type="text"
                  id="procedure-name"
                  value={editedProcedure.name}
                  onChange={updateName}
                ></input>
              </div>
              <div className="procedure-field">
                <label>Description</label>
                <KendoReactEditor
                  tools={editorTools}
                  defaultContent={editedProcedure.description || ''}
                  onChange={updateDescription}
                  onMount={addEditorContentStyles}
                  resizable={true}
                  contentStyle={{ height: '100%' }}
                  style={{ resize: 'vertical' }}
                />
              </div>
              <div className="editor-buttons">
                <button className="btn right" onClick={saveChanges}>
                  Save
                </button>
                <button
                  className="btn btn-text-only right"
                  onClick={cancelChanges}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="editor readonly">
              {!readOnly && (
                <div
                  className="editor-hover-button fa-light fa-pencil"
                  title="Edit"
                  onClick={() => setIsEditMode(true)}
                ></div>
              )}
              <div className="procedure-field">
                <h3>{procedure.name}</h3>
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: procedure.description
                }}
              ></div>
            </div>
          )}
          {!isPublicView && !readOnly && (
            <div className="procedure-action-add-button">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => addAction(-1, 0)}
              >
                Add Action
              </button>
            </div>
          )}
          {procedure.actions
            .filter((action) => action.active)
            .map((action, index) => (
              <React.Fragment key={action.id}>
                <ProcedureActionEditor
                  index={index + 1}
                  action={action}
                  readOnly={readOnly}
                  isEditing={action.id === EmptyGuid}
                  editorTools={editorTools}
                  addEditorContentStyles={addEditorContentStyles}
                  persistChanges={actionUpdated}
                  showImageView={index === imageViewIndex}
                  setImageViewIndex={imageViewNavigationChange}
                ></ProcedureActionEditor>
                {!isPublicView && !readOnly && (
                  <div className="procedure-action-add-button">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => addAction(index, action.order + 1)}
                    >
                      Add Action
                    </button>
                  </div>
                )}
              </React.Fragment>
            ))}
        </div>
        {!isMobile && (
          <div className="editors-right-column">
            <div className="right-column-actions">
              {canEdit && !readOnly && !isPublicView && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setIsDetailsModalOpen(true)}
                >
                  Details
                </button>
              )}
              {canPublish && !readOnly && !isPublicView && (
                <button
                  className="btn btn-primary"
                  onClick={() => setIsPublishModalOpen(true)}
                >
                  Publish
                </button>
              )}
              {canEdit && readOnly && !isPublicView && (
                <button className="btn btn-primary" onClick={goToEditMode}>
                  Edit
                </button>
              )}
              {isPublicView && (
                <>
                  <div className="public-actions-copy-link">
                    <ClipboardCopy
                      label="Copy link "
                      copyText={window.location.href}
                    ></ClipboardCopy>
                  </div>
                  <a href="/login" className="blue-text">
                    Sign in to Flowingly
                  </a>
                </>
              )}
            </div>
          </div>
        )}
        {isPublishModalOpen && (
          <PublishProcedureModal
            procedure={procedure}
            onClose={() => setIsPublishModalOpen(false)}
            onPublish={handlePublishedProcedure}
          />
        )}
        {isDetailsModalOpen && (
          <ProcedureDetailsModal
            procedure={procedure}
            onClose={() => setIsDetailsModalOpen(false)}
            onSave={(updatedProcedure) => {
              setProcedure(updatedProcedure);
              setIsDetailsModalOpen(false);
            }}
          />
        )}
      </div>
    )
  );
};

export default ProcedureEditor;
